import React, { useState, useEffect } from "react";
import DashboardLayout from "../layouts/Dashboardlayout";
import bg from "../../src/assets/svg/bg.svg";
import gradient from "../../src/assets/svg/gradient.svg";
import { AnimatedRobot } from "../components/ui/loader/AnimatedRobot";
import { Textarea } from "../components/ui/loader/TextArea";
import OpenAI from "openai";
import undo from "../../src/assets/svg/undo.svg";
import CodeViewer from "../components/CodeViewer";
import quillbot from "../assets/svg/quillbot.svg";
import send from "../assets/svg/send.svg";
import copy from "../assets/svg/copy.svg";
import aicode from "../../src/assets/svg/Sparkle.svg";

export default function CodeEdgeAI() {
  const initialState = {
    loading: false,
    results: null,
    code: "",
    language: "",
    showInputs: false,
    currentStep: "language",
    suggestions: null,
  };

  const [state, setState] = useState(initialState);
  const { loading, results, code, language, showInputs, currentStep } = state;

  const isFormValid = code.trim() && language.trim();

  const openAi = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  const handleSend = async () => {
    if (!code.trim()) return;

    setState((prev) => ({
      ...prev,
      showInputs: true,
    }));
  };

  const handleInputSubmit = (text) => {
    if (!text.trim()) return;

    if (currentStep === "language") {
      setState((prev) => ({
        ...prev,
        language: text,
        loading: true, // Start loading immediately
      }));

      // Call handleSubmit directly after setting language
      setTimeout(() => {
        handleSubmit(text);
      }, 0);
    }
  };

  const handleSubmit = async (languageText = "") => {
    const finalLanguage = languageText || language;

    try {
      console.log("Compiling code:", { code, language: finalLanguage });
      const [result, suggestions] = await Promise.all([
        codeCompiler(code, finalLanguage),
        codeFixer(code, finalLanguage),
      ]);
      console.log("Received result:", result);

      setState((prev) => ({
        ...prev,
        results: result,
        suggestions: suggestions,
        language: finalLanguage,
        loading: false,
      }));

      console.log("State updated with results and suggestions");
    } catch (error) {
      console.error("Compilation error:", error);
      setState((prev) => ({
        ...prev,
        results: "An error occurred. Please try again.",
        loading: false,
      }));
    }
  };

  const handleUndo = () => {
    setState(initialState);
  };

  const handleInputChange = (field) => (e) => {
    setState((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const codeCompiler = async (code, language) => {
    const responseGPT = await openAi.chat.completions.create({
      model: "gpt-3.5-turbo",
      temperature: 0,
      max_tokens: 1000,
      messages: [
        {
          role: "system",
          content: `You are a helpful assistant that compiles code from one programming language to another.
    - You specialize in translating code between various programming languages, such as Python, JavaScript, Java, C++, and more.
    - You provide accurate translations of code snippets, ensuring that syntax and functionality are preserved across different languages.
    - **ALWAYS** think step by step before providing a translated code snippet.
    - You clarify any ambiguities in code before proceeding with translation and ask for additional details if the code provided is incomplete or unclear.
    - Provide concise explanations of any changes made during the translation process to help users understand how the code functions in the target language.
    - Ensure the translated code is optimized for readability and follows best practices for the target programming language.
    - Use formatting to enhance readability: *text* for bold, _text_ for italics, '-' for bullet points.
    - Stay focused on the task of code translation; avoid engaging in unrelated conversations or extended chat.
    - Offer additional tips or suggestions to improve code quality when relevant.
    - Be patient and provide supportive feedback to encourage users to learn from the translations provided.`,
        },
        {
          role: "user",
          content: `I need assistance with a coding task. Here are the details of my request:
 Compile the following code ${code} to a code in this ${language} and provide the output and show just the output nothing more

      \`\`\`
      ${code}
      \`\`\`

      Please review the code and let me know how I can improve it or fix any issues.`,
        },
      ],
    });

    return responseGPT.choices[0].message.content;
  };

  const codeFixer = async (code, language) => {
    const responseGPT = await openAi.chat.completions.create({
      model: "gpt-3.5-turbo",
      temperature: 0,
      max_tokens: 1000,
      messages: [
        {
          role: "system",
          content: `You are a helpful assistant that corrects code.
    - Your primary task is to review and correct code snippets provided by users, identifying errors and suggesting improvements.
    - You work with various programming languages, such as Python, JavaScript, Java, C++, and more.
    - **ALWAYS** think step by step before providing corrections or suggestions.
    - Provide clear explanations for each correction to help users understand what went wrong and how to fix it.
    - Offer optimized and efficient solutions while adhering to best practices for the specific programming language.
    - Use formatting to enhance readability: *text* for bold, _text_ for italics, '-' for bullet points.
    - Avoid using technical jargon unless necessary; keep explanations simple and user-friendly.
    - Ask for clarification if the code snippet is incomplete or unclear before making corrections.
    - Be supportive and encouraging, providing constructive feedback to help users improve their coding skills.
    - Focus on code correction tasks; avoid engaging in off-topic conversations.
    - Offer tips or suggestions for improving code quality, readability, and performance when relevant.`,
        },
        {
          role: "user",
          content: `I need assistance with a coding task. Here are the details of my request:
You will be provided with a piece of code in ${language}, and your task is to provide ideas for efficiency improvements based on the code provided. All your output should be based on the ${language} provided.

    \`\`\`
    ${code}
    \`\`\`

    Please review the code and let me know how I can improve it or fix any issues.`,
        },
      ],
    });

    return responseGPT.choices[0].message.content;
  };

  useEffect(() => {
    // Prevent ResizeObserver loop limit exceeded error
    const resizeObserverError = (error) => {
      if (error.message.includes("ResizeObserver")) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.style.display = "none";
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.style.display = "none";
        }
      }
    };

    window.addEventListener("error", resizeObserverError);

    return () => {
      window.removeEventListener("error", resizeObserverError);
    };
  }, []);

  const FormattedResponse = ({ content }) => {
    const formatResponse = (text) => {
      if (!text) return [];

      const sections = text.split(/```(\w*)\n/);
      return sections.map((section, index) => {
        if (index % 3 === 0) {
          const lines = section.split("\n");
          return lines.map((line, lineIndex) => {
            if (line.startsWith("- ")) {
              return (
                <li key={lineIndex} className="ml-4 mb-2 text-[#F2F4F7]">
                  {line.substring(2)}
                </li>
              );
            } else if (line.startsWith("#")) {
              const level = line.match(/^#+/)[0].length;
              const text = line.replace(/^#+\s/, "");
              const HeaderTag = `h${level}`;
              return (
                <HeaderTag
                  key={lineIndex}
                  className={`text-white font-semibold mb-2 ${
                    level === 1
                      ? "text-2xl"
                      : level === 2
                      ? "text-xl"
                      : "text-lg"
                  }`}
                >
                  {text}
                </HeaderTag>
              );
            } else if (line.match(/\*\*.*\*\*/)) {
              return (
                <p key={lineIndex} className="mb-2 text-[#F2F4F7]">
                  {line.split(/(\*\*.*?\*\*)/).map((part, i) => {
                    if (part.startsWith("**") && part.endsWith("**")) {
                      return (
                        <strong key={i} className="font-semibold">
                          {part.slice(2, -2)}
                        </strong>
                      );
                    }
                    return part;
                  })}
                </p>
              );
            }
            return (
              <p key={lineIndex} className="mb-2 text-[#F2F4F7]">
                {line}
              </p>
            );
          });
        } else if (index % 3 === 1) {
          return null;
        } else {
          return (
            <div
              key={index}
              className="bg-[#1E1F20] rounded-lg p-4 my-4 font-mono text-sm text-[#F2F4F7] overflow-x-auto "
            >
              <pre className="whitespace-pre-wrap">{section}</pre>
            </div>
          );
        }
      });
    };

    return (
      <div className="w-full h-full overflow-y-auto bg-transparent border-none scrollbar-hide">
        <div className="p-4">{formatResponse(content)}</div>
      </div>
    );
  };

  if (showInputs) {
    return (
      <DashboardLayout>
        <div className="h-screen w-full bg-cover bg-center flex flex-col justify-center items-center relative">
          <img className="absolute right-0 top-0" src={gradient} />
          <div className="w-full h-full flex relative ">
            <div className="w-[40%] h-full lg:flex hidden flex-col justify-between gap-6 relative  px-6 pb-[230px] pt-6">
              <div className="flex flex-col gap-6 overflow-y-auto">
                <div className="w-full flex justify-end ">
                  <div className=" w-[479px] border border-white/10 rounded-[12px] bg-[#3F3F46] p-4">
                    <p className="text-white">{code}</p>
                  </div>
                </div>

                {(currentStep === "language" || language) && (
                  <>
                    <div className="flex items-center gap-2 ">
                      <div className="w-[56px] h-[56px] quillbot rounded-full flex justify-center items-center">
                        <div className="w-[35px] h-[35px] rounded-full flex justify-center items-center bg-[white]/20 border border-[white]/10 drop-shadow-[0px_1.5px_4px_rgba(16,25,40,0.07)] backdrop-blur-[20px]">
                          <img
                            src={quillbot}
                            className="w-6 h-6"
                            alt="quillbot"
                          />
                        </div>
                      </div>
                      <h4 className="text-white text-[18px]">
                        Enter Language:
                      </h4>
                    </div>
                    {language && (
                      <div className="flex justify-end">
                        <div className="overflow-hidden w-[479px] border border-white/10 rounded-[12px] bg-[#3F3F46] p-4">
                          <p className="text-white">{language}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="absolute bottom-0 right-0 w-full p-6 bg-[#ffffff0b] backdrop-blur-[12px]">
                <div className="w-full h-full relative">
                  <div className="w-full md:h-[195px] h-[108px] rounded-[12px] bgt p-5">
                    <Textarea
                      placeholder={`Enter ${currentStep} here...`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleInputSubmit(e.target.value);
                          e.target.value = "";
                        }
                      }}
                      disabled={loading || results}
                    />
                  </div>

                  <div
                    onClick={(e) => {
                      if (loading || results) return;
                      const textarea =
                        e.currentTarget.parentElement.querySelector("textarea");
                      if (textarea.value.trim()) {
                        handleInputSubmit(textarea.value);
                        textarea.value = "";
                      }
                    }}
                    className={`cursor-pointer absolute bottom-5 right-5 w-12 h-12 rounded-full flex justify-center items-center border border-white/10 bg-white/10 shadow-[inset_0px_8px_12px_rgba(255,255,255,0.4),inset_0px_20px_30px_rgba(255,255,255,0.4),inset_0px_-10px_75px_rgba(255,255,255,0.2)] backdrop-blur-[30px] ${
                      loading || results ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <img src={send} className="w-6 h-6" alt="send" />
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:w-[60%] w-full bgtt rounded-[12px]  my-6 mr-6 relative ">
              <div className="w-full h-[72px] bg-[#27272A] rounded-t-[12px] border border-[#27272A] flex justify-between items-center px-6">
                <p className="text-white text-[20px]">Result</p>
                <div className="flex items-center gap-2">
                  <div className="cursor-pointer w-10 h-10 rounded-[8px] bg-[#ffffff0f] border border-[#ffffff14] flex justify-center items-center">
                    <img src={copy} className="w-6 h-6" alt="quillbot" />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center h-full text-white gap-6">
                  <AnimatedRobot className="md:w-[240px] w-[200px] md:h-[140px] h-[100px]" />
                  <p className="md:text-lg text-sm text-[#F2F4F7] font-medium text-center">
                    Debugging the bugs... and maybe questioning their life{" "}
                    <br />
                    choices 🐞. Hang tight, we're almost there!
                  </p>
                </div>
              ) : results ? (
                <>
                  <div className="h-[calc(100vh-50%)] overflow-auto pt-10">
                    <CodeViewer code={results} language={language} />
                  </div>
                  <div className="absolute bottom-0 left-0 h-[40%] p-5 w-full text-white">
                    <div className="w-full h-full bgh p-4">
                      <div className="w-full h-full bg-[#121314] rounded-[11.264px] flex flex-col p-4">
                        <div className="flex gap-[8px] pb-5 items-center">
                          <img src={aicode} alt="aicode" />
                          <h5 className="text-white">Suggestion</h5>
                        </div>
                        <FormattedResponse content={state.suggestions} />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p className="text-white/60">Results will appear here...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="lg:h-screen w-full bg-cover bg-center flex flex-col md:justify-center md:items-center relative">
        {" "}
        <img className="absolute right-0 top-0" src={gradient} />
        <div className="">
          <div className="flex flex-col justify-center items-center gap-4 text-center md:px-0 px-4">
            <div className="w-[100px] h-[100px] quillbot rounded-full flex justify-center items-center">
              <div className="w-16 h-16 rounded-full flex justify-center items-center bg-[white]/20 border border-[white]/10 drop-shadow-[0px_1.5px_4px_rgba(16,25,40,0.07)] backdrop-blur-[20px]">
                <img src={quillbot} className="w-10 h-10" alt="quillbot" />
              </div>
            </div>
            <h2 className="md:text-[48px] text-[36px] font-semibold bg md:leading-[60px] leading-[40px] mt-6">
              What do you <br className="md:hidden block" /> want to translate?
            </h2>
            <p className="text-[#F2F4F7] mt-4">
              Prompt, run, edit, and translate codes
            </p>
          </div>

          <div className="relative w-full mt-10">
            <div className="lg:w-[936px] w-full h-full relative ">
              <div className="w-full md:h-[195px] h-[108px] rounded-[12px] bgt p-5 ">
                <Textarea
                  placeholder="Ask Code Translator AI to translate"
                  onChange={handleInputChange("code")}
                  value={code}
                />
              </div>

              <div
                onClick={handleSend}
                disabled={!isFormValid}
                class=" cursor-pointer absolute bottom-5 right-5 w-12 h-12 rounded-full flex justify-center items-center border border-white/10 bg-white/10 shadow-[inset_0px_8px_12px_rgba(255,255,255,0.4),inset_0px_20px_30px_rgba(255,255,255,0.4),inset_0px_-10px_75px_rgba(255,255,255,0.2)] backdrop-blur-[30px]"
              >
                <img src={send} className="w-6 h-6" alt="send" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
